export type PendingRegistrationChannel = 'email' | 'phone' | 'both';

export interface PendingRegistrationEmailState {
  identifier: string;
  maskedIdentifier: string;
}

export interface PendingRegistrationPhoneState {
  identifier: string;
  maskedIdentifier: string;
  /** Phone register only — UX/routing local state, not sent to request-otp API. */
  challengeJti?: string;
  hint?: 'SMS_DISABLED';
  /** G4 — SMS already delivered at register; pending can skip send step. */
  otpSent?: boolean;
  /** Phone OTP challenge TTL (not email top-level expiresAt when channel is both). */
  expiresAt?: string;
}

export interface PendingRegistrationState {
  channel: PendingRegistrationChannel;
  channels: Array<'email' | 'phone'>;
  maskedIdentifier: string;
  /**
   * Primary identifier for legacy single-channel flows (resend / request-otp).
   * For `both`, prefer phone when present so SMS OTP can proceed immediately.
   */
  identifier: string;
  email?: PendingRegistrationEmailState;
  phone?: PendingRegistrationPhoneState;
  expiresAt?: string;
  challengeJti?: string;
  hint?: 'SMS_DISABLED';
}

/**
 * Session key for pending registration.
 * Always pass `PLATFORM_CHROME_TENANT_CODE` (`__platform__`) for platform chrome —
 * yields `rpapp:customer:pending-registration:__platform__` (never a `:platform` literal).
 */
function storageKey(tenantCode: string): string {
  return `rpapp:customer:pending-registration:${tenantCode}`;
}

function normalizePending(
  raw: PendingRegistrationState
): PendingRegistrationState | null {
  if (raw.channel !== 'email' && raw.channel !== 'phone' && raw.channel !== 'both') {
    return null;
  }
  let channels: Array<'email' | 'phone'>;
  if (Array.isArray(raw.channels) && raw.channels.length > 0) {
    channels = raw.channels;
  } else if (raw.channel === 'both') {
    channels = ['email', 'phone'];
  } else {
    channels = [raw.channel];
  }
  return {
    ...raw,
    channels,
  };
}

export function savePendingRegistration(
  tenantCode: string,
  state: PendingRegistrationState
): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(storageKey(tenantCode), JSON.stringify(state));
  } catch {
    // Ignore quota / private mode failures.
  }
}

export function readPendingRegistration(
  tenantCode: string
): PendingRegistrationState | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey(tenantCode));
    if (raw === null || raw.length === 0) {
      return null;
    }
    return normalizePending(JSON.parse(raw) as PendingRegistrationState);
  } catch {
    return null;
  }
}

export function clearPendingRegistration(tenantCode: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(storageKey(tenantCode));
  } catch {
    // Ignore.
  }
}

/**
 * After one dual-registration channel is verified, keep only the remaining
 * channel in pending storage (PENDING_OTHER_CHANNEL).
 */
export function buildRemainingPendingRegistration(input: {
  current: PendingRegistrationState | null;
  remainingChannel: 'email' | 'phone';
  maskedPendingIdentifier: string;
}): PendingRegistrationState {
  const { current, remainingChannel, maskedPendingIdentifier } = input;

  if (remainingChannel === 'email') {
    let emailIdentifier = '';
    if (current?.email?.identifier !== undefined && current.email.identifier.length > 0) {
      emailIdentifier = current.email.identifier;
    } else if (
      current !== null &&
      (current.channel === 'email' || current.channel === 'both') &&
      current.identifier.length > 0
    ) {
      emailIdentifier = current.identifier;
    }
    return {
      channel: 'email',
      channels: ['email'],
      maskedIdentifier: maskedPendingIdentifier,
      identifier: emailIdentifier,
      email: {
        identifier: emailIdentifier,
        maskedIdentifier: maskedPendingIdentifier,
      },
    };
  }

  let phoneIdentifier = '';
  if (current?.phone?.identifier !== undefined && current.phone.identifier.length > 0) {
    phoneIdentifier = current.phone.identifier;
  } else if (
    current !== null &&
    (current.channel === 'phone' || current.channel === 'both') &&
    current.identifier.length > 0
  ) {
    phoneIdentifier = current.identifier;
  }
  const phone: PendingRegistrationPhoneState = {
    identifier: phoneIdentifier,
    maskedIdentifier: maskedPendingIdentifier,
  };
  if (current?.phone?.challengeJti !== undefined) {
    phone.challengeJti = current.phone.challengeJti;
  } else if (current?.challengeJti !== undefined) {
    phone.challengeJti = current.challengeJti;
  }
  if (current?.phone?.hint !== undefined) {
    phone.hint = current.phone.hint;
  } else if (current?.hint !== undefined) {
    phone.hint = current.hint;
  }
  if (current?.phone?.otpSent === true) {
    phone.otpSent = true;
  }
  if (current?.phone?.expiresAt !== undefined) {
    phone.expiresAt = current.phone.expiresAt;
  }
  return {
    channel: 'phone',
    channels: ['phone'],
    maskedIdentifier: maskedPendingIdentifier,
    identifier: phoneIdentifier,
    phone,
  };
}
